import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import {
  fetchTransactionsFiltered,
  getBodyPayloadFiltered,
  invalidatePendingTransactionsFetches,
} from 'components/Transactions/func';
import {
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  ENTER_KEY_DOWN,
  PAGE_DEFAULT,
  TRANSACTION_TABLE_SORT_ORDER_INITIAL,
} from 'constants';
import { refNumberPhoneValid, searchTextValid } from 'helpers';
import useAuth from 'hooks/useAuth';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';
import useStore from '../../../../hooks/useStore';
import { SET_REFRESH_TRANSACTION_LIST } from '../../../../store/action';
import CustomSearchDropdown from './components/CustomSearchDropdown/ver2';

const TRANSACTION_SEARCH_INPUT_FIELD = 'transaction-search-input-field';

const REF_NUMBER_LENGTH_MAX_OLD = 15;
export const REF_NUMBER_LENGTH_MAX = 15;

const normalizeSearchText = (value) =>
  (value ?? '').trim().split(' ').filter(Boolean).join(' ');

const SearchTransaction = ({
  applyFiltered,
  currentSearchBy,
  setCurrentSearchBy,
  searchValue,
  setSearchValue,
  transactions,
  setTransactions,
  setTotalPages,
  setCurrentPage,
  setSortOrder,
  setTotalElements,
  setFetching = () => {},
  setSearched = () => {},
  isSearchInvalid,
  setSearchSubmittedInvalid,
}) => {
  const { token } = useAuth();
  const { t } = useLang();
  const { dispatch } = useStore();

  // True from an invalid submit until the text becomes valid or empty again.
  const invalidSubmitActiveRef = useRef(false);

  const containerRef = useRef(null);

  // The valid text last submitted (whitespace-normalised, as the debounce
  // rewrites it), so a blur does not repeat that search.
  const lastSubmittedRef = useRef(null);

  useEffect(() => {
    if (!searchValue) lastSubmittedRef.current = null;
  }, [searchValue]);

  // Bumped by a search-type reset, a cleared input or an empty-submit flush so
  // a debounce queued before it is dropped.
  const resetGenerationRef = useRef(0);

  // Reloads the list with an empty search value.
  const refetchUnsearchedList = () => {
    dispatch({ type: SET_REFRESH_TRANSACTION_LIST, payload: true });
    setSearched(false);
    // if (isEmpty(transactions)) return;

    const bodyPayload = getBodyPayloadFiltered(applyFiltered);

    // setTransactions(null);
    fetchTransactionsFiltered({
      token,
      body: bodyPayload,
      page: PAGE_DEFAULT,
      setTransactions,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };

  // Latest text and reload for the deferred clear-reload, which fires after
  // renders that may have changed the text, filters or search type.
  const searchValueRef = useRef(searchValue);
  searchValueRef.current = searchValue;
  const refetchUnsearchedListRef = useRef(refetchUnsearchedList);
  refetchUnsearchedListRef.current = refetchUnsearchedList;
  // True from a clear until its reload runs or is dropped.
  const clearReloadPendingRef = useRef(false);
  // True while a clear shows the spinner only to keep this input mounted (on
  // desktop with no filters, an empty list and empty text hide it unless
  // fetching); unmounting would cancel the queued reload and lose the next
  // keystroke.
  const clearHeldSpinnerRef = useRef(false);
  const releaseClearSpinner = () => {
    if (!clearHeldSpinnerRef.current) return;
    clearHeldSpinnerRef.current = false;
    setFetching(false);
  };
  const runClearReload = () => {
    clearReloadPendingRef.current = false;
    clearHeldSpinnerRef.current = false;
    setFetching(true);
    refetchUnsearchedListRef.current();
  };

  // Reloads DEBOUNCE_SEC after the last clear if the box is then empty, so a
  // transient empty value (Backspace-style IME) neither blanks nor reloads.
  const { debounce: debounceClearReload } = useDebounceCallback(
    (generation) => {
      if (generation !== resetGenerationRef.current) return;
      if (searchValueRef.current) {
        clearReloadPendingRef.current = false;
        releaseClearSpinner();
        return;
      }
      runClearReload();
    },
    DEBOUNCE_SEC,
  );

  const { debounce } = useDebounceCallback((value, generation) => {
    if (generation !== resetGenerationRef.current) return;
    if (invalidSubmitActiveRef.current) return;

    if (currentSearchBy === 'receiverName') {
      if (value) {
        value = value.trim();

        let values = value.split(' ');

        if (!arrayUtils.isArrayEmpty(values)) {
          values = arrayUtils.removeElement(values, '');

          value = values.join(' ');
        }

        setSearchValue(value);
      }
    }

    // Reached with an empty value only when trimming left nothing.
    if (!value) {
      refetchUnsearchedList();
    }
  }, DEBOUNCE_SEC);

  const handleOnChange = (e) => {
    const { value } = e.target;
    setSearchSubmittedInvalid(false);
    if (!value || searchTextValid(value)) {
      invalidSubmitActiveRef.current = false;
    }
    if (!value) {
      // The reload runs on its own timer, so a later keystroke cannot restart
      // it; text present when it fires cancels it. The bump drops any debounce
      // queued by earlier text.
      resetGenerationRef.current += 1;
      clearReloadPendingRef.current = true;
      if (isEmpty(transactions)) {
        // A response already in flight can clear isFetching and, if it leaves
        // the list empty, hide this input (desktop, no filters, empty text),
        // cancelling its queued reload; drop it.
        invalidatePendingTransactionsFetches();
        clearHeldSpinnerRef.current = true;
        setFetching(true);
      }
      debounceClearReload(resetGenerationRef.current);
    }

    if (
      currentSearchBy === 'refNumber' &&
      value?.length < REF_NUMBER_LENGTH_MAX + 1
    ) {
      setSearchValue(value);
      if (value) releaseClearSpinner();
    }

    if (currentSearchBy === 'receiverName') {
      setSearchValue(value);

      if (value) {
        releaseClearSpinner();
        debounce(value, resetGenerationRef.current);
      }
    }
  };

  const searchByRefNumber = async (value) => {
    if (
      currentSearchBy === 'refNumber' &&
      (value?.length <= REF_NUMBER_LENGTH_MAX ||
        value?.length <= REF_NUMBER_LENGTH_MAX_OLD)
    ) {
      const valueValid = refNumberPhoneValid(value);

      if (valueValid) {
        let bodyPayload = getBodyPayloadFiltered(applyFiltered);

        bodyPayload = {
          ...bodyPayload,
          refNumber: valueValid,
        };

        setTransactions(null);
        fetchTransactionsFiltered({
          token,
          body: bodyPayload,
          page: PAGE_DEFAULT,
          setTransactions,
          setCurrentPage,
          setTotalPages,
          setTotalElements,
          setFetching,
        });
      } else {
        setTransactions(null);
        setFetching(false);
      }
    }
  };
  const searchByReceiverName = async (value) => {
    if (currentSearchBy === 'receiverName') {
      let bodyPayload = getBodyPayloadFiltered(applyFiltered);

      bodyPayload = {
        ...bodyPayload,
        receiverName: value,
      };

      setTransactions(null);
      fetchTransactionsFiltered({
        token,
        body: bodyPayload,
        page: PAGE_DEFAULT,
        setTransactions,
        setCurrentPage,
        setTotalPages,
        setTotalElements,
        setFetching,
      });
    }
  };
  const handleSearchTransaction = () => {
    // An empty submit while a clear-reload is pending runs that reload now.
    if (!searchValue && clearReloadPendingRef.current) {
      resetGenerationRef.current += 1;
      runClearReload();
      return;
    }

    if (!searchTextValid(searchValue)) {
      lastSubmittedRef.current = null;
      setSearchSubmittedInvalid(true);
      invalidSubmitActiveRef.current = true;
      // Show the no-result state without a request. A dropped in-flight fetch
      // skips its setFetching(false), so clear it here. [] (not null) keeps
      // the page's initial-load effect off.
      invalidatePendingTransactionsFetches();
      setTransactions([]);
      setFetching(false);
      setSortOrder(TRANSACTION_TABLE_SORT_ORDER_INITIAL);
      return;
    }

    lastSubmittedRef.current = normalizeSearchText(searchValue);
    setSearchSubmittedInvalid(false);
    invalidSubmitActiveRef.current = false;

    setFetching(true);

    setSearched(true);

    searchByRefNumber(searchValue);

    searchByReceiverName(searchValue);

    setSortOrder(TRANSACTION_TABLE_SORT_ORDER_INITIAL);
  };

  // Changing the search type resets the search: clear the text, validation
  // state and sort, drop any queued debounce, then reload the list with an
  // empty search value.
  const handleChangeSearchBy = (keySearch) => {
    const isSameType = keySearch === currentSearchBy;

    if (isSameType && !searchValue) return;

    resetGenerationRef.current += 1;
    clearReloadPendingRef.current = false;
    clearHeldSpinnerRef.current = false;

    setCurrentSearchBy(keySearch);
    setSearchValue('');
    setSearchSubmittedInvalid(false);
    invalidSubmitActiveRef.current = false;
    lastSubmittedRef.current = null;
    setSearched(false);
    setSortOrder(TRANSACTION_TABLE_SORT_ORDER_INITIAL);
    dispatch({ type: SET_REFRESH_TRANSACTION_LIST, payload: true });
    setFetching(true);

    fetchTransactionsFiltered({
      token,
      body: getBodyPayloadFiltered(applyFiltered),
      page: PAGE_DEFAULT,
      setTransactions,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };

  const handleOnKeyDown = (e) => {
    const { key } = e;

    if (key === ENTER_KEY_DOWN) {
      handleSearchTransaction();
    }
  };

  // Leaving the field submits like Enter. Blank text, a window or tab switch,
  // focus moving into this control (the search-by dropdown) and a repeat of the
  // last submitted text are left alone.
  const handleOnBlur = (e) => {
    if (!document.hasFocus()) return;
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (!searchValue?.trim()) return;
    if (normalizeSearchText(searchValue) === lastSubmittedRef.current) return;

    handleSearchTransaction();
  };

  return (
    <SearchTransactionStyled ref={containerRef}>
      {/* Keep focus in the input on a pointer press, so the dropdown does not blur-submit first. */}
      <div
        style={{ display: 'contents' }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <CustomSearchDropdown
          searchBy={currentSearchBy}
          setSearchBy={handleChangeSearchBy}
          setSearchValue={setSearchValue}
          setSearched={setSearched}
        />
      </div>
      <SearchInput
        name={TRANSACTION_SEARCH_INPUT_FIELD}
        type="text"
        value={searchValue || ''}
        placeholder={t('label_search_input')}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onBlur={handleOnBlur}
        readOnly={!currentSearchBy}
      />
      <SearchIconStyled
        src={SearchIcon}
        width={24}
        height={24}
        alt=""
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleSearchTransaction}
      />
      {isSearchInvalid && (
        <SearchErrorStyled role="alert">
          {t('form_msg_search_validation')}
        </SearchErrorStyled>
      )}
    </SearchTransactionStyled>
  );
};

const SearchTransactionStyled = styled.div`
  position: relative;
  height: 44px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: unset;
  }
`;

const SearchInput = styled.input`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-black);
  border: 1px solid var(--ds-c-blue-remox-light);
  border-radius: 8px;
  background: var(--ds-c-white);
  width: calc(100% - 52px);
  height: 44px;
  padding: 10px;
  padding-left: 132px;
  padding-right: 40px;
  float: left;

  &:focus,
  &:focus-visible {
    border: 1px solid #b0b6bd !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 300px;
  }
`;
const SearchIconStyled = styled.img`
  position: absolute;
  top: 10px;
  left: calc(100% - 84px);
  cursor: pointer;
  user-select: none;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    left: 266px;
  }
`;

const SearchErrorStyled = styled.p`
  position: absolute;
  top: 48px;
  left: 0;
  width: calc(100% - 32px);
  margin: 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    font-size: 14px;
    line-height: 17px;
  }
`;

export default SearchTransaction;
